/**
 * Форма работы внутри операции (AK-525): исполнитель · техника · дата · площадь.
 *
 * «Работа» (`works`) — это строка факта: кто, на чём, когда и сколько сделал.
 * Сумма площадей работ пересчитывается триггером в `operations.works_total_area_ha`
 * и определяет «выполнена полностью / частично» в списке операций. Первая
 * работа переводит запланированную операцию в «выполняется» (авто-переход,
 * AK-524) — поэтому после сохранения обязательно перечитываем саму операцию.
 *
 * Исполнитель и техника пока свободным текстом: тенантского API парка техники
 * (`company_machinery`) наружу ещё нет, а ждать его — значит держать факт
 * невводимым. Бэкенд принимает и `*_name_text`, и `*_id`; переход на выбор из
 * справочника не сломает уже введённые записи.
 */
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { Work, WorkBody } from './operationsApi';

interface Props {
  open: boolean;
  /** Редактируемая работа; `null` — создание новой. */
  work: Work | null;
  /** Площадь по умолчанию для новой работы (обычно план операции). */
  defaultAreaHa?: number | null;
  saving?: boolean;
  error?: string | null;
  onSubmit: (body: WorkBody) => void;
  onClose: () => void;
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export function WorkFormDialog({
  open,
  work,
  defaultAreaHa,
  saving,
  error,
  onSubmit,
  onClose,
}: Props) {
  const [executor, setExecutor] = useState('');
  const [machine, setMachine] = useState('');
  const [date, setDate] = useState(today());
  const [area, setArea] = useState('');
  const [comment, setComment] = useState('');
  const [touched, setTouched] = useState(false);

  // Пере-инициализация при каждом открытии: иначе форма показывает данные
  // прошлой работы (классический источник «я правил не ту строку»).
  useEffect(() => {
    if (!open) return;
    setExecutor(work?.executor_name ?? '');
    setMachine(work?.machine_name_text ?? '');
    setDate(work?.work_date ?? today());
    setArea(
      work ? String(work.area_ha) : defaultAreaHa != null ? String(defaultAreaHa) : '',
    );
    setComment(work?.comment ?? '');
    setTouched(false);
  }, [open, work, defaultAreaHa]);

  const areaNum = Number(area.replace(',', '.'));
  const areaValid = area.trim() !== '' && Number.isFinite(areaNum) && areaNum >= 0;
  const dateValid = !!date;
  const valid = areaValid && dateValid;

  const submit = () => {
    setTouched(true);
    if (!valid) return;
    onSubmit({
      executor_name_text: executor.trim() || null,
      machine_name_text: machine.trim() || null,
      work_date: date,
      area_ha: areaNum,
      comment: comment.trim() || null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{work ? 'Правка работы' : 'Добавить работу'}</DialogTitle>
          <DialogDescription>
            Кто и на чём работал, когда и сколько гектаров сделал.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="work-date">Дата работы</Label>
            <Input
              id="work-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
            {touched && !dateValid && (
              <p className="text-xs text-destructive">Укажите дату.</p>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="work-area">Площадь, га</Label>
            <Input
              id="work-area"
              inputMode="decimal"
              placeholder="0"
              value={area}
              onChange={(e) => setArea(e.target.value)}
            />
            {touched && !areaValid && (
              <p className="text-xs text-destructive">
                Площадь — число не меньше нуля.
              </p>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="work-executor">Исполнитель</Label>
            <Input
              id="work-executor"
              placeholder="ФИО механизатора"
              value={executor}
              onChange={(e) => setExecutor(e.target.value)}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="work-machine">Техника</Label>
            <Input
              id="work-machine"
              placeholder="Например, МТЗ-1221 № 42"
              value={machine}
              onChange={(e) => setMachine(e.target.value)}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="work-comment">Комментарий</Label>
            <Input
              id="work-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Отмена
          </Button>
          <Button onClick={submit} disabled={saving}>
            {saving ? 'Сохраняем…' : 'Сохранить'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default WorkFormDialog;
